import { useEffect, useMemo, useState } from 'react';

import {
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { googleFontValue } from '@realadvisor/common-types';
import { Search } from 'lucide-react';
import { type Control, Controller } from 'react-hook-form';

import {
  dropdownShadow,
  searchFieldSx,
} from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { RaSelectChevronIcon } from '@modules/_shared/components/form/RaSelect';
import {
  type GoogleFont,
  STANDARD_FONTS,
  filterFonts,
  fontLabel,
  googleFontsFromRows,
} from '@modules/reports/shared/reportFonts';
import { useLocale } from '@shared/hooks/locale';

type ReportFontSelectProps = {
  name: string;
  control: Control<any>;
};

const useGoogleFonts = (enabled: boolean): readonly GoogleFont[] => {
  const [fonts, setFonts] = useState<readonly GoogleFont[]>([]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    import('@modules/reports/shared/googleFonts.json').then(module => {
      if (!cancelled) {
        setFonts(googleFontsFromRows(module.default));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return fonts;
};

const FontSelectField: React.FC<{
  value: string | null | undefined;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const googleFonts = useGoogleFonts(open);

  const standardMatches = useMemo(
    () => filterFonts(STANDARD_FONTS, font => font.label, query),
    [query],
  );
  const googleMatches = useMemo(
    () => filterFonts(googleFonts, font => font[0], query),
    [googleFonts, query],
  );

  const current = value ?? '';
  const currentIsListed =
    standardMatches.some(font => font.value === current) ||
    googleMatches.some(font => googleFontValue(font[0], font[1]) === current);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const pick = (nextValue: string) => {
    onChange(nextValue);
    close();
  };

  const firstMatch = (): string | null => {
    const standard = standardMatches[0];
    if (standard != null) {
      return standard.value;
    }
    const google = googleMatches[0];
    return google != null ? googleFontValue(google[0], google[1]) : null;
  };

  return (
    <Select
      value={current}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={close}
      onChange={event => {
        if (typeof event.target.value === 'string') {
          pick(event.target.value);
        }
      }}
      fullWidth
      variant="outlined"
      size="small"
      displayEmpty
      renderValue={selected => (
        <Typography noWrap sx={{ fontSize: 'inherit' }}>
          {fontLabel(String(selected))}
        </Typography>
      )}
      IconComponent={RaSelectChevronIcon}
      sx={{ background: 'var(--mui-palette-background-paper)' }}
      MenuProps={{
        autoFocus: false,
        elevation: 0,
        slotProps: {
          paper: {
            sx: {
              maxHeight: 360,
              mt: 0.5,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              boxShadow: dropdownShadow,
              '& .MuiListSubheader-root': { fontSize: '0.75rem' },
            },
          },
        },
      }}
    >
      <ListSubheader
        sx={{
          top: 0,
          zIndex: 2,
          p: 1,
          bgcolor: 'background.paper',
          lineHeight: 'normal',
        }}
      >
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          size="small"
          sx={searchFieldSx}
          placeholder={t('Search fonts')}
          value={query}
          onChange={event => setQuery(event.target.value)}
          onClick={event => event.stopPropagation()}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              const next = firstMatch();
              if (next != null) {
                pick(next);
              }
            }
            if (event.key !== 'Escape') {
              event.stopPropagation();
            }
          }}
          slotProps={{
            input: { startAdornment: <Search size={16} /> },
            htmlInput: { autoComplete: 'one-time-code' },
          }}
        />
      </ListSubheader>
      {!currentIsListed && current !== '' && (
        <MenuItem value={current} sx={{ display: 'none' }}>
          {fontLabel(current)}
        </MenuItem>
      )}
      {standardMatches.length > 0 && (
        <ListSubheader>{t('Standard fonts')}</ListSubheader>
      )}
      {standardMatches.map(font => (
        <MenuItem key={font.value} value={font.value}>
          {font.label}
        </MenuItem>
      ))}
      {googleMatches.length > 0 && (
        <ListSubheader>{t('Google Fonts')}</ListSubheader>
      )}
      {googleMatches.map(([family, generic]) => (
        <MenuItem key={family} value={googleFontValue(family, generic)}>
          {family}
        </MenuItem>
      ))}
      {standardMatches.length === 0 && googleMatches.length === 0 && (
        <MenuItem disabled value="">
          {t('No results')}
        </MenuItem>
      )}
    </Select>
  );
};

export const ReportFontSelect: React.FC<ReportFontSelectProps> = ({
  name,
  control,
}) => {
  const { t } = useLocale();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl fullWidth>
          <RaLabel label={t('Font')} />
          <FontSelectField value={field.value} onChange={field.onChange} />
        </FormControl>
      )}
    />
  );
};
